import { useEffect, useRef, useState } from "react";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  Download,
  Dumbbell,
  Eye,
  Layers,
  PartyPopper,
  Trophy,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Checkbox,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  cn,
  MarkdownContent,
  toast,
} from "@cliently/ui";
import { useTranslation } from "react-i18next";
import {
  buildCustomWorkoutHeadline,
  groupStepsByContiguousGroupTitle,
  parseBlockGroupMeta,
} from "@cliently/schemas";
import {
  isStepCompleted,
  usePortalPrograms,
  useUpdateProgramAssignmentStep,
  type PortalAssignmentSession,
} from "../hooks/use-programs";
import { useClientProfile } from "../hooks/use-client-profile";
import { api } from "../lib/api";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { ExerciseGuideDialog } from "../components/programs/ExerciseGuideDialog";
import { AdventureMapIllustration } from "../assets/undraw/AdventureMapIllustration";
import { EmptyCartIllustration } from "../assets/undraw/EmptyCartIllustration";
import { ClientSessionDetailModal } from "../components/programs/ClientSessionDetailModal";
import { ProgramCalendarView } from "../components/programs/ProgramCalendarView";
import { ProgramBlockMedia } from "../components/programs/ProgramBlockMedia";
import { shouldUseProgramCalendarView } from "../components/programs/program-calendar-utils";

const LAST_MODIFIED_PROGRAM_STORAGE_KEY = "cliently:last-modified-program-id";

const getMoodPalette = (title: string, description: string | null) => {
  const text = `${title} ${description ?? ""}`.toLowerCase();
  if (
    text.includes("vente") ||
    text.includes("business") ||
    text.includes("relance")
  ) {
    return "from-amber-500/10 to-orange-500/5 border-amber-500/30";
  }
  if (
    text.includes("sante") ||
    text.includes("santé") ||
    text.includes("fitness") ||
    text.includes("sport")
  ) {
    return "from-emerald-500/10 to-lime-500/5 border-emerald-500/30";
  }
  if (
    text.includes("focus") ||
    text.includes("mind") ||
    text.includes("habitude")
  ) {
    return "from-violet-500/10 to-indigo-500/5 border-violet-500/30";
  }
  return "from-primary/10 to-primary/5 border-primary/20";
};

const clamp = (value: number) => Math.min(100, Math.max(0, value));



/** Base de nom de fichier sans extension — caractères interdits Windows / navigateurs. */
const sanitizeProgramPdfBasename = (raw: string): string => {
  const forbidden = new Set('\\/:*?"<>|'.split(""));
  let out = "";
  for (const c of raw) {
    const code = c.charCodeAt(0);
    out += code <= 31 || forbidden.has(c) ? "-" : c;
  }
  const s = out.replace(/\s+/g, " ").trim().replace(/\.+$/u, "");
  return s.slice(0, 180);
}



const buildProgramPdfDownloadFilename = (programTitle: string | undefined | null, fallbackBasename: string): string => {
  const fromTitle = sanitizeProgramPdfBasename(programTitle?.trim() ?? "");
  const fallback = sanitizeProgramPdfBasename(fallbackBasename);
  const base = fromTitle || fallback || "document";
  return `${base}.pdf`;
}



export const ProgramsPage = () => {
  const { t, i18n } = useTranslation();
  const { data = [], isLoading } = usePortalPrograms();
  const { data: profile } = useClientProfile();
  const clientNotesEnabled =
    profile?.org?.clientProgramSessionNotesEnabled !== false;
  const [openSession, setOpenSession] = useState<{
    session: PortalAssignmentSession;
    clientId: string;
  } | null>(null);
  const updateStep = useUpdateProgramAssignmentStep();
  const { mutateAsync: updateStepAsync } = updateStep;
  const [draftByStep, setDraftByStep] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<"active" | "closed">("active");
  const [celebratingStepId, setCelebratingStepId] = useState<string | null>(
    null,
  );
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(
    null,
  );
  /** Mobile : cartes accordéon. Desktop : masqué au profit de la liste + panneau. */
  const [expandedProgramIds, setExpandedProgramIds] = useState<
    Record<string, boolean>
  >({});
  const [lastModifiedProgramId, setLastModifiedProgramId] = useState<
    string | null
  >(() => {
    return window.localStorage.getItem(LAST_MODIFIED_PROGRAM_STORAGE_KEY);
  });
  /** Par groupe : `true` = étapes visibles. Les groupes terminés sont repliés par défaut. */
  const [groupAccordionOpen, setGroupAccordionOpen] = useState<
    Record<string, boolean>
  >({});
  const [bulkProgramId, setBulkProgramId] = useState<string | null>(null);

  const renderSessionsTimeline = (
    rowId: string,
    rowClientId: string,
    sessions: PortalAssignmentSession[],
    completedCyclesCount: number,
  ) => {
    const currentCycle = completedCyclesCount;
    const currentSessions = sessions
      .filter((s) => s.cycleIndex === currentCycle)
      .sort((a, b) =>
        a.weekIndex !== b.weekIndex
          ? a.weekIndex - b.weekIndex
          : a.position - b.position,
      );
    if (currentSessions.length <= 1) return null;
    const byWeek = new Map<number, PortalAssignmentSession[]>();
    for (const s of currentSessions) {
      const k = s.weekIndex ?? 0;
      const arr = byWeek.get(k) ?? [];
      arr.push(s);
      byWeek.set(k, arr);
    }
    const nextSession = currentSessions.find((s) => s.status === "PENDING");
    const styleByStatus = (status: PortalAssignmentSession["status"]) => {
      switch (status) {
        case "DONE":
          return {
            border: "border-emerald-500/40",
            dot: "bg-emerald-500",
            text: "text-emerald-600",
            label: t("programs.statusDone", "Complète"),
          };
        case "MISSED":
          return {
            border: "border-rose-600/40",
            dot: "bg-rose-600",
            text: "text-rose-600",
            label: t("programs.statusMissed", "Manquée"),
          };
        case "PARTIAL":
          return {
            border: "border-amber-500/40",
            dot: "bg-amber-500",
            text: "text-amber-600",
            label: t("programs.statusPartial", "Partielle"),
          };
        case "PENDING":
        default:
          return {
            border: "border-border/70",
            dot: "bg-muted-foreground/40",
            text: "text-muted-foreground",
            label: t("programs.statusPending", "À venir"),
          };
      }
    };
    return (
      <div className="space-y-3" key={`sessions-timeline-${rowId}`}>
        {nextSession && (
          <div className="border-primary/50 bg-primary/5 relative rounded-xl border p-3">
            <p className="text-primary text-[10px] font-bold uppercase tracking-[0.18em]">
              {t("programs.nextSessionBadge", "Prochaine séance")}
            </p>
            <div className="mt-1 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-bold">
                  {nextSession.title}
                </p>
                {nextSession.scheduledAt && (
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {new Date(nextSession.scheduledAt).toLocaleDateString(
                      i18n.language,
                      { day: "2-digit", month: "long", year: "numeric" },
                    )}
                  </p>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  setOpenSession({
                    session: nextSession,
                    clientId: rowClientId,
                  })
                }
              >
                {t("programs.openSession", "Ouvrir")}
              </Button>
            </div>
          </div>
        )}
        <div>
          <p className="text-muted-foreground mb-2 text-[11px] font-bold uppercase tracking-[0.14em]">
            {t("programs.sessionsListLabel", "Suivi des séances")}
          </p>
          <div className="space-y-3">
            {Array.from(byWeek.entries())
              .sort((a, b) => a[0] - b[0])
              .map(([week, items]) => (
                <div key={`w-${week}`} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <p className="text-muted-foreground text-[10px] font-bold uppercase tracking-[0.18em]">
                      {t("programs.weekLabel", "Semaine {{n}}", {
                        n: week + 1,
                      })}
                    </p>
                    <div className="bg-border/60 h-px flex-1" />
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((s, idx) => {
                      const ss = styleByStatus(s.status);
                      return (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() =>
                            setOpenSession({
                              session: s,
                              clientId: rowClientId,
                            })
                          }
                          className={cn(
                            "bg-background/80 hover:border-primary/60 group rounded-lg border px-3 py-2.5 text-left transition-colors",
                            ss.border,
                          )}
                        >
                          <div className="text-muted-foreground text-[10px] font-bold uppercase tracking-[0.14em]">
                            {t("programs.sessionNumber", "Séance {{n}}", {
                              n: String(idx + 1).padStart(2, "0"),
                            })}
                          </div>
                          <div className="text-foreground mt-0.5 text-sm font-semibold leading-tight">
                            {s.title}
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                            <span
                              className={cn(
                                "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                                ss.dot,
                              )}
                            />
                            <span className={ss.text}>{ss.label}</span>
                            {s.status !== "PENDING" && (
                              <span className="text-muted-foreground ml-auto">
                                {s.progressPercent}%
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };
  const [confirmCheckAll, setConfirmCheckAll] = useState<{
    programId: string;
    clientId: string;
    steps: { id: string; type: string; isDone: boolean }[];
  } | null>(null);
  const [viewingExercise, setViewingExercise] = useState<{
    name: string;
    nameFr: string | null;
    images: string[];
    youtubeUrl: string | null;
    instructions: string[];
    instructionsFr: string[];
    primaryMuscles: string[];
    category: string | null;
    level: string | null;
    equipment: string | null;
  } | null>(null);
  const celebrationTimeoutRef = useRef<number | null>(null);

  const isAllProsMode =
    useOrgStore((s) => s.activeClientId) === ALL_CLIENTS_KEY;

  const fetchAssignmentPdfBlob = async (assignmentId: string) => {
    const path = isAllProsMode
      ? `/portal/aggregate/program-assignments/${assignmentId}/pdf`
      : `/portal/program-assignments/${assignmentId}/pdf`;
    const { data } = await api.get(path, { responseType: "blob" });
    return data as Blob;
  };

  const sortProgramsByLastModified = <T extends { id: string }>(
    programs: T[],
  ) => {
    if (!lastModifiedProgramId) return programs;
    return [...programs].sort((a, b) => {
      if (a.id === lastModifiedProgramId) return -1;
      if (b.id === lastModifiedProgramId) return 1;
      return 0;
    });
  };

  const activePrograms = sortProgramsByLastModified(
    data.filter((row) => row.progress < 100),
  );
  const closedPrograms = sortProgramsByLastModified(
    data.filter((row) => row.progress >= 100),
  );
  const displayedPrograms = tab === "active" ? activePrograms : closedPrograms;
  const pendingStepId = updateStep.variables?.id;
  const totalPrograms = data.length;
  const averageProgress =
    totalPrograms > 0
      ? Math.round(
          data.reduce((acc, row) => acc + row.progress, 0) / totalPrograms,
        )
      : 0;

  const readDraft = (stepId: string, fallback: string) =>
    draftByStep[stepId] ?? fallback;
  const getDurationLabel = (row: (typeof displayedPrograms)[number]) => {
    const durationSteps = row.steps.filter(
      (step) => step.type === "DURATION" && step.targetValue !== null,
    );
    const hasDurationSteps = durationSteps.length > 0;
    if (!hasDurationSteps) return null;
    const total = durationSteps.reduce(
      (acc, step) => acc + (step.targetValue ?? 0),
      0,
    );
    const unit = durationSteps[0]?.targetUnit ?? "min";
    return `${total} ${unit}`;
  };
  const markProgramAsLastModified = (programId: string) => {
    setLastModifiedProgramId(programId);
    window.localStorage.setItem(LAST_MODIFIED_PROGRAM_STORAGE_KEY, programId);
  };

  const toggleStepDone = (
    programId: string,
    stepId: string,
    isDone: boolean,
    assignmentClientId: string,
  ) => {
    if (updateStep.isPending && pendingStepId === stepId) return;
    markProgramAsLastModified(programId);

    if (!isDone) {
      setCelebratingStepId(stepId);
      if (celebrationTimeoutRef.current) {
        window.clearTimeout(celebrationTimeoutRef.current);
      }
      celebrationTimeoutRef.current = window.setTimeout(() => {
        setCelebratingStepId(null);
      }, 1200);
    }

    updateStep.mutate({
      id: stepId,
      data: { isDone: !isDone },
      assignmentClientId,
    });
  };

  const setProgramCheckboxStepsDone = async (
    programId: string,
    assignmentClientId: string,
    steps: { id: string; type: string; isDone: boolean }[],
    wantDone: boolean,
  ) => {
    const targets = steps.filter(
      (s) => s.type === "CHECKBOX" && s.isDone !== wantDone,
    );
    if (targets.length === 0) return;
    markProgramAsLastModified(programId);
    setBulkProgramId(programId);
    try {
      for (const step of targets) {
        await updateStepAsync({
          id: step.id,
          data: { isDone: wantDone },
          assignmentClientId,
        });
      }
    } finally {
      setBulkProgramId(null);
    }
  };

  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) {
        window.clearTimeout(celebrationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (displayedPrograms.length === 0) {
      setExpandedProgramIds({});
      return;
    }
    setExpandedProgramIds((prev) => {
      const ids = new Set(displayedPrograms.map((p) => p.id));
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        if (!ids.has(id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [displayedPrograms]);

  useEffect(() => {
    if (displayedPrograms.length === 0) {
      setSelectedProgramId(null);
      return;
    }
    setSelectedProgramId((current) => {
      if (current && displayedPrograms.some((p) => p.id === current)) {
        return current;
      }
      if (
        lastModifiedProgramId &&
        displayedPrograms.some((p) => p.id === lastModifiedProgramId)
      ) {
        return lastModifiedProgramId;
      }
      return displayedPrograms[0]!.id;
    });
  }, [displayedPrograms, lastModifiedProgramId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="bg-muted h-32 animate-pulse" />
        ))}
      </div>
    );
  }

  const completedCount = closedPrograms.length;
  const totalCount = data.length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          {t("programs.title")}
        </h1>
        <p className="text-foreground/80 text-sm">{t("programs.subtitle")}</p>
      </div>

      {totalCount > 0 ? (
        <Card className="border-border/60 bg-card/80">
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-xs font-medium">
                    {t("programs.globalProgress")}
                  </p>
                  <p className="text-sm font-bold tabular-nums">
                    {averageProgress}%
                  </p>
                </div>
                <div
                  className="bg-muted/80 ring-border/50 h-2.5 w-full overflow-hidden rounded-full ring-1 ring-inset"
                  role="progressbar"
                  aria-valuenow={averageProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className={cn(
                      "h-full rounded-full transition-[width] duration-500 ease-out",
                      averageProgress >= 100
                        ? "bg-emerald-500"
                        : averageProgress > 0
                          ? "from-primary to-primary/85 bg-linear-to-r"
                          : "bg-muted-foreground/30",
                    )}
                    style={{ width: `${clamp(averageProgress)}%` }}
                  />
                </div>
                <p className="text-muted-foreground text-[11px]">
                  {completedCount}/{totalCount}{" "}
                  {completedCount === 1
                    ? "programme terminé"
                    : "programmes terminés"}
                </p>
              </div>
              {averageProgress >= 100 ? (
                <div className="text-emerald-500">
                  <Trophy className="h-8 w-8" />
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="bg-muted/50 inline-flex gap-1 rounded-lg p-1">
        {(["active", "closed"] as const).map((tabKey) => (
          <Button
            key={tabKey}
            type="button"
            variant="ghost"
            onClick={() => setTab(tabKey)}
            className={cn(
              "h-auto rounded-md px-4 py-1.5 text-sm font-medium shadow-none transition-all",
              tab === tabKey
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tabKey === "active"
              ? `${t("programs.active")} (${activePrograms.length})`
              : `${t("programs.closed")} (${closedPrograms.length})`}
          </Button>
        ))}
      </div>

      {displayedPrograms.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <AdventureMapIllustration size={112} className="mx-auto" />
            <p className="text-sm font-medium">
              {tab === "active"
                ? t("programs.emptyActiveTitle")
                : t("programs.emptyClosedTitle")}
            </p>
            <p className="text-muted-foreground max-w-md text-xs">
              {tab === "active"
                ? t("programs.emptyActiveDescription")
                : t("programs.emptyClosedDescription")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="grid items-start gap-4 lg:hidden">
          {displayedPrograms.map((row) =>
            (() => {
              const isProgramExpanded = expandedProgramIds[row.id] === true;
              const isProgramDone = row.progress >= 100;
              const visibilityExpiresAt = row.visibilityExpiresAt;
              const hasVisibilityExpiration = visibilityExpiresAt !== null;
              const durationLabel = getDurationLabel(row);
              const hasDurationLabel = Boolean(durationLabel);
              const stepGroups = (() => {
                const ordered = [...row.steps].sort(
                  (a, b) => a.position - b.position,
                );
                return groupStepsByContiguousGroupTitle(ordered).map((g) => {
                  const meta = parseBlockGroupMeta(g.items[0]?.groupDescription);
                  return {
                    key: `${g.startIndex}-${g.key}`,
                    label: g.label,
                    steps: g.items,
                    headline: meta.workoutType
                      ? buildCustomWorkoutHeadline(
                          meta.workoutType,
                          meta.workoutParams ?? {},
                        )
                      : null,
                    instruction: meta.instruction,
                  };
                });
              })();
              const hasNamedGroups = stepGroups.some((group) => group.label);

              return (
                <Card
                  key={row.id}
                  className={cn(
                    "border-border/70 bg-linear-to-br transition-all duration-200",
                    getMoodPalette(row.program.title, row.program.description),
                    isProgramExpanded
                      ? "shadow-md"
                      : "hover:border-primary/30 hover:shadow-primary/10 hover:shadow-md",
                  )}
                >
                  <CardHeader
                    className="cursor-pointer pb-2"
                    onClick={() =>
                      setExpandedProgramIds((prev) => ({
                        ...prev,
                        [row.id]: !prev[row.id],
                      }))
                    }
                  >
                    <div className="relative flex items-start gap-2">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <CardTitle className="text-lg leading-snug">
                            {row.program.title}
                          </CardTitle>
                          {row.program.contentMode === "PDF" ? (
                            <Badge
                              variant="secondary"
                              className="shrink-0 text-[10px]"
                            >
                              {t("programs.deliveryModePdf", "PDF")}
                            </Badge>
                          ) : null}
                          {isProgramDone ? (
                            <Badge
                              variant="default"
                              className="shrink-0 text-[10px]"
                            >
                              {t("status.done")}
                            </Badge>
                          ) : null}
                        </div>
                        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                          <span>
                            {t("programs.assignedOn")}{" "}
                            {new Date(row.assignedAt).toLocaleDateString(
                              i18n.language,
                            )}
                          </span>
                          {hasVisibilityExpiration ? (
                            <span>
                              {t("programs.visibleUntil")}{" "}
                              {new Date(visibilityExpiresAt).toLocaleDateString(
                                i18n.language,
                              )}
                            </span>
                          ) : null}
                          {hasDurationLabel ? (
                            <span>Durée : {durationLabel}</span>
                          ) : null}
                          {row.program.org?.name ? (
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {row.program.org.name}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <ChevronDown
                        className={cn(
                          "text-muted-foreground mt-1 h-5 w-5 shrink-0 transition-transform duration-200",
                          isProgramExpanded ? "rotate-180" : "",
                        )}
                      />
                    </div>
                    <div className="mt-3 space-y-1.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-muted-foreground text-[11px] font-medium">
                          {t("programs.progress")}
                        </span>
                        <span className="text-xs font-semibold tabular-nums tracking-tight">
                          {row.progress}%
                        </span>
                      </div>
                      <div
                        className="bg-muted/80 ring-border/50 h-2 w-full overflow-hidden rounded-full ring-1 ring-inset"
                        role="progressbar"
                        aria-valuenow={row.progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${t("programs.progress")} ${row.progress}%`}
                      >
                        <div
                          className={cn(
                            "h-full rounded-full transition-[width] duration-500 ease-out",
                            row.progress >= 100
                              ? "bg-emerald-500"
                              : row.progress > 0
                                ? "from-primary to-primary/85 bg-linear-to-r"
                                : "bg-muted-foreground/30",
                          )}
                          style={{ width: `${clamp(row.progress)}%` }}
                        />
                      </div>
                    </div>
                    {!isProgramExpanded && row.program.description?.trim() ? (
                      <p className="text-muted-foreground mt-2 line-clamp-2 text-xs leading-relaxed">
                        {row.program.description
                          .replace(/[#*_~`>]/g, "")
                          .slice(0, 150)}
                      </p>
                    ) : null}
                  </CardHeader>
                  {isProgramExpanded ? (
                    <CardContent className="space-y-4 pt-0">
                      {row.program.description?.trim() ? (
                        <MarkdownContent
                          content={row.program.description}
                          className="text-muted-foreground text-sm"
                        />
                      ) : null}
                      {row.program.contentMode === "PDF" ? (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const previewWindow = window.open(
                                "about:blank",
                                "_blank",
                              );
                              if (!previewWindow) {
                                toast.error(
                                  t(
                                    "programs.pdfPopupBlocked",
                                    "Autorise les fenêtres pop-up pour afficher le PDF, ou utilise le téléchargement.",
                                  ),
                                );
                                return;
                              }
                              try {
                                const blob = await fetchAssignmentPdfBlob(
                                  row.id,
                                );
                                const url = URL.createObjectURL(blob);
                                previewWindow.location.href = url;
                                window.setTimeout(
                                  () => URL.revokeObjectURL(url),
                                  120_000,
                                );
                              } catch {
                                previewWindow.close();
                                toast.error(
                                  t(
                                    "programs.pdfOpenFailed",
                                    "Impossible d'ouvrir le PDF.",
                                  ),
                                );
                              }
                            }}
                          >
                            <Eye className="h-4 w-4" />
                            {t("programs.viewPdf", "Voir le PDF")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const blob = await fetchAssignmentPdfBlob(
                                  row.id,
                                );
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = buildProgramPdfDownloadFilename(
                                  row.program.title,
                                  t(
                                    "programs.downloadPdfFilenameFallback",
                                    "programme",
                                  ),
                                );
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              } catch {
                                /* ignore */
                              }
                            }}
                          >
                            <Download className="h-4 w-4" />
                            {t("programs.downloadPdf", "Télécharger")}
                          </Button>
                        </div>
                      ) : null}
                      {row.completedCyclesCount > 0 ? (
                        <div className="border-border/60 bg-muted/20 space-y-2 rounded-lg border px-3 py-2.5">
                          <p className="text-muted-foreground text-xs font-medium">
                            {t("programs.completionHistoryTitle")}
                          </p>
                          <p className="text-foreground text-xs">
                            {t("programs.completionCount", {
                              count: row.completedCyclesCount,
                            })}
                          </p>
                          <ul className="text-muted-foreground list-inside list-disc space-y-1 text-xs">
                            {(row.completionCycles ?? []).map((c) => (
                              <li key={c.id}>
                                {t("programs.completionHistoryItem", {
                                  cycle: c.cycleIndex,
                                  percent: c.progressPercent,
                                  date: new Date(
                                    c.completedAt,
                                  ).toLocaleDateString(i18n.language),
                                })}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {row.program.contentMode !== "PDF" &&
                        !shouldUseProgramCalendarView(
                          row.sessions ?? [],
                          row.completedCyclesCount,
                        ) &&
                        (() => {
                          const programCheckboxSteps = row.steps.filter(
                            (s) => s.type === "CHECKBOX",
                          );
                          const allProgramCheckboxesDone =
                            programCheckboxSteps.length > 0 &&
                            programCheckboxSteps.every((s) => s.isDone);
                          const bulkBusyThisProgram = bulkProgramId === row.id;
                          if (programCheckboxSteps.length === 0) return null;
                          const doneCount = programCheckboxSteps.filter(
                            (s) => s.isDone,
                          ).length;
                          return (
                            <div className="border-border/60 bg-muted/25 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                <p className="text-muted-foreground text-xs font-medium">
                                  {t("programs.cardBulkSteps")}
                                </p>
                                <span className="text-muted-foreground/70 text-[10px] tabular-nums">
                                  {doneCount}/{programCheckboxSteps.length}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-[11px]">
                                  {t("programs.groupCheckAll")}
                                </span>
                                <Switch
                                  checked={allProgramCheckboxesDone}
                                  disabled={
                                    bulkBusyThisProgram || updateStep.isPending
                                  }
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setConfirmCheckAll({
                                        programId: row.id,
                                        clientId: row.clientId,
                                        steps: row.steps,
                                      });
                                    } else {
                                      void setProgramCheckboxStepsDone(
                                        row.id,
                                        row.clientId,
                                        row.steps,
                                        false,
                                      );
                                    }
                                  }}
                                  aria-label={t("programs.groupCheckAll")}
                                />
                              </div>
                            </div>
                          );
                        })()}
                      {row.program.contentMode !== "PDF" &&
                      shouldUseProgramCalendarView(
                        row.sessions ?? [],
                        row.completedCyclesCount,
                      ) ? (
                        <ProgramCalendarView
                          programId={row.id}
                          title={row.program.title}
                          sessions={row.sessions ?? []}
                          completedCyclesCount={row.completedCyclesCount}
                          clientId={row.clientId}
                          onViewExerciseGuide={setViewingExercise}
                          onMarkModified={() => markProgramAsLastModified(row.id)}
                        />
                      ) : (
                        <>
                      {renderSessionsTimeline(
                        row.id,
                        row.clientId,
                        row.sessions ?? [],
                        row.completedCyclesCount,
                      )}
                      {row.steps.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 py-6 text-center">
                          <EmptyCartIllustration
                            size={112}
                            className="mx-auto"
                          />
                          <p className="text-muted-foreground max-w-sm text-xs leading-relaxed">
                            {t("programs.noStep")}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {stepGroups.map((group) => {
                            const groupComplete =
                              group.steps.length > 0 &&
                              group.steps.every(isStepCompleted);
                            const hasGroupHeader = Boolean(
                              group.label || hasNamedGroups,
                            );
                            const doneInGroup =
                              group.steps.filter(isStepCompleted).length;
                            const accordionKey = `${row.id}::${group.key}`;
                            const defaultGroupExpanded = !groupComplete;
                            const groupExpanded = hasGroupHeader
                              ? (groupAccordionOpen[accordionKey] ??
                                defaultGroupExpanded)
                              : true;
                            const groupStepsDomId = `program-${row.id}-group-${group.key.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

                            return (
                              <div key={group.key} className="space-y-3">
                                {hasGroupHeader ? (
                                  <div
                                    className={cn(
                                      "overflow-hidden rounded-lg border",
                                      groupComplete
                                        ? "border-emerald-500/35 bg-emerald-500/10"
                                        : "bg-muted/60 border-border/60",
                                    )}
                                  >
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      className={cn(
                                        "relative flex h-auto w-full items-center justify-start gap-2.5 rounded-none px-3 py-2.5 text-left font-normal shadow-none transition-colors sm:px-4 sm:py-3",
                                        "focus-visible:ring-ring focus-visible:ring-2",
                                        groupComplete
                                          ? "hover:bg-emerald-500/15"
                                          : "hover:bg-muted/80",
                                      )}
                                      aria-expanded={groupExpanded}
                                      aria-controls={groupStepsDomId}
                                      aria-label={
                                        groupExpanded
                                          ? t(
                                              "programs.groupAccordionCollapse",
                                              "Replier le groupe",
                                            )
                                          : t(
                                              "programs.groupAccordionExpand",
                                              "Déplier le groupe",
                                            )
                                      }
                                      onClick={() => {
                                        setGroupAccordionOpen((prev) => {
                                          const current =
                                            prev[accordionKey] ??
                                            defaultGroupExpanded;
                                          return {
                                            ...prev,
                                            [accordionKey]: !current,
                                          };
                                        });
                                      }}
                                    >
                                      {groupComplete ? (
                                        <CheckCircle2
                                          className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400"
                                          aria-hidden
                                        />
                                      ) : (
                                        <Layers
                                          className="text-primary h-5 w-5 shrink-0"
                                          aria-hidden
                                        />
                                      )}
                                      <div className="flex min-w-0 flex-1 items-center gap-2.5 pr-9 sm:pr-10">
                                        <p className="text-foreground min-w-0 flex-1 text-sm font-bold leading-snug sm:text-base">
                                          {group.label ??
                                            t(
                                              "programs.ungroupedSection",
                                              "Étapes sans groupe",
                                            )}
                                        </p>
                                        <span
                                          className={cn(
                                            "shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums",
                                            groupComplete
                                              ? "text-foreground border-emerald-500/45 bg-emerald-500/15"
                                              : "text-foreground/80 bg-background/80 border-border/60",
                                          )}
                                        >
                                          {t(
                                            "programs.groupStepRatio",
                                            "{{done}}/{{total}}",
                                            {
                                              done: doneInGroup,
                                              total: group.steps.length,
                                            },
                                          )}
                                        </span>
                                      </div>
                                      <ChevronDown
                                        className={cn(
                                          "text-muted-foreground absolute right-3 top-2.5 h-5 w-5 shrink-0 transition-transform duration-200 sm:right-4 sm:top-3",
                                          groupExpanded ? "rotate-180" : "",
                                        )}
                                        aria-hidden
                                      />
                                    </Button>
                                    {groupExpanded &&
                                    (group.headline ||
                                      group.instruction ||
                                      group.steps[0]?.groupMediaUrl?.trim()) ? (
                                      <div className="border-border/40 space-y-3 border-t px-3 py-3 pl-10 sm:px-4 sm:pl-12">
                                        {group.headline ? (
                                          <p className="text-foreground text-sm font-semibold leading-snug">
                                            {group.headline}
                                          </p>
                                        ) : null}
                                        {group.instruction ? (
                                          <p
                                            className={cn(
                                              "whitespace-pre-line text-sm font-medium leading-snug",
                                              groupComplete
                                                ? "text-foreground/90"
                                                : "text-foreground/80",
                                            )}
                                          >
                                            {group.instruction}
                                          </p>
                                        ) : null}
                                        {group.steps[0]?.groupMediaUrl?.trim() ? (
                                          <ProgramBlockMedia
                                            url={group.steps[0].groupMediaUrl}
                                          />
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}
                                <div
                                  id={
                                    hasGroupHeader ? groupStepsDomId : undefined
                                  }
                                  className={cn(
                                    "grid gap-2",
                                    "grid-cols-1",
                                    hasGroupHeader && "ml-4 sm:ml-8",
                                    hasGroupHeader &&
                                      !groupExpanded &&
                                      "hidden",
                                  )}
                                >
                                  {group.steps.map((step) =>
                                    (() => {
                                      const isCheckboxStep =
                                        step.type === "CHECKBOX";
                                      const isStepPending =
                                        updateStep.isPending &&
                                        pendingStepId === step.id;

                                      return (
                                        <div
                                          key={step.id}
                                          className={cn(
                                            "relative rounded-xl border p-3 transition-all",
                                            step.isDone
                                              ? "border-emerald-500/30 bg-emerald-500/5"
                                              : "border-border/80 bg-background/80 hover:border-primary/30 hover:bg-background",
                                            isCheckboxStep
                                              ? "cursor-pointer select-none"
                                              : "",
                                          )}
                                          role={
                                            isCheckboxStep
                                              ? "button"
                                              : undefined
                                          }
                                          tabIndex={isCheckboxStep ? 0 : -1}
                                          aria-pressed={
                                            isCheckboxStep
                                              ? step.isDone
                                              : undefined
                                          }
                                          onClick={() => {
                                            if (!isCheckboxStep) return;
                                            toggleStepDone(
                                              row.id,
                                              step.id,
                                              step.isDone,
                                              row.clientId,
                                            );
                                          }}
                                          onKeyDown={(e) => {
                                            if (!isCheckboxStep) return;
                                            if (
                                              e.key !== "Enter" &&
                                              e.key !== " "
                                            )
                                              return;
                                            e.preventDefault();
                                            toggleStepDone(
                                              row.id,
                                              step.id,
                                              step.isDone,
                                              row.clientId,
                                            );
                                          }}
                                        >
                                          {celebratingStepId === step.id ? (
                                            <div className="pointer-events-none absolute -top-2 right-2 flex animate-bounce items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700 shadow-sm">
                                              <PartyPopper className="h-3.5 w-3.5" />
                                              Bravo !
                                            </div>
                                          ) : null}
                                          <div className="flex items-start gap-2">
                                            <div className="pt-px">
                                              <Checkbox
                                                checked={step.isDone}
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                className="mt-0.5 shrink-0"
                                                disabled={
                                                  !isCheckboxStep ||
                                                  isStepPending
                                                }
                                                onCheckedChange={() =>
                                                  toggleStepDone(
                                                    row.id,
                                                    step.id,
                                                    step.isDone,
                                                    row.clientId,
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="min-w-0 flex-1 space-y-1.5">
                                              <div className="flex flex-wrap items-center justify-between gap-1.5">
                                                <span
                                                  className={cn(
                                                    "line-clamp-1 text-sm font-semibold leading-snug sm:text-[0.9rem]",
                                                    step.isDone
                                                      ? "text-muted-foreground line-through"
                                                      : "",
                                                  )}
                                                >
                                                  {step.title}
                                                </span>
                                                {step.exercise ? (
                                                  <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setViewingExercise(
                                                        step.exercise,
                                                      );
                                                    }}
                                                    className="text-primary hover:bg-primary/10 inline-flex h-auto shrink-0 gap-1 px-1.5 py-0.5 text-[10px] font-medium shadow-none"
                                                    title={t(
                                                      "exerciseGuide.viewExercise",
                                                    )}
                                                  >
                                                    <Dumbbell className="h-3.5 w-3.5" />
                                                  </Button>
                                                ) : null}
                                                {step.targetValue !== null ? (
                                                  <Badge
                                                    variant="outline"
                                                    className="text-[10px]"
                                                  >
                                                    {step.targetValue}{" "}
                                                    {step.targetUnit ?? ""}
                                                  </Badge>
                                                ) : null}
                                              </div>
                                              {step.description ? (
                                                <MarkdownContent
                                                  content={step.description}
                                                  className="text-foreground/90 text-sm"
                                                />
                                              ) : null}
                                              {step.type === "TEXT" ? (
                                                <textarea
                                                  value={readDraft(
                                                    step.id,
                                                    step.valueText ?? "",
                                                  )}
                                                  onChange={(e) =>
                                                    setDraftByStep((prev) => ({
                                                      ...prev,
                                                      [step.id]: e.target.value,
                                                    }))
                                                  }
                                                  onBlur={() => (
                                                    markProgramAsLastModified(
                                                      row.id,
                                                    ),
                                                    updateStep.mutate({
                                                      id: step.id,
                                                      data: {
                                                        valueText: readDraft(
                                                          step.id,
                                                          step.valueText ?? "",
                                                        ),
                                                      },
                                                      assignmentClientId:
                                                        row.clientId,
                                                    })
                                                  )}
                                                  placeholder={t(
                                                    "programs.enterProgress",
                                                  )}
                                                  className="bg-muted/50 focus-visible:border-ring focus-visible:ring-ring min-h-20 w-full rounded-md border border-transparent px-3 py-2 text-xs focus-visible:ring-2"
                                                />
                                              ) : null}
                                              {step.type === "NUMBER" ||
                                              step.type === "DURATION" ? (
                                                <input
                                                  type="number"
                                                  inputMode="decimal"
                                                  value={readDraft(
                                                    step.id,
                                                    step.valueNumber?.toString() ??
                                                      "",
                                                  )}
                                                  onChange={(e) =>
                                                    setDraftByStep((prev) => ({
                                                      ...prev,
                                                      [step.id]: e.target.value,
                                                    }))
                                                  }
                                                  onBlur={() => {
                                                    markProgramAsLastModified(
                                                      row.id,
                                                    );
                                                    const raw = readDraft(
                                                      step.id,
                                                      step.valueNumber?.toString() ??
                                                        "",
                                                    );
                                                    const parsed =
                                                      raw.trim() === ""
                                                        ? null
                                                        : Number(raw);
                                                    updateStep.mutate({
                                                      id: step.id,
                                                      data: {
                                                        valueNumber:
                                                          Number.isFinite(
                                                            parsed,
                                                          )
                                                            ? parsed
                                                            : null,
                                                      },
                                                      assignmentClientId:
                                                        row.clientId,
                                                    });
                                                  }}
                                                  className="bg-muted/50 focus-visible:border-ring focus-visible:ring-ring h-10 w-full rounded-md border border-transparent px-3 text-xs focus-visible:ring-2"
                                                />
                                              ) : null}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })(),
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                        </>
                      )}
                    </CardContent>
                  ) : null}
                </Card>
              );
            })(),
          )}
        </div>
        <div className="hidden space-y-6 lg:grid lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start lg:gap-8 lg:space-y-0">
          <section
            className="space-y-3"
            aria-labelledby="programs-program-list-title"
          >
            <h2
              id="programs-program-list-title"
              className="text-foreground px-0.5 text-sm font-semibold tracking-tight"
            >
              {t("programs.sectionServiceList")}
            </h2>
            <div className="flex flex-col gap-2">
              {displayedPrograms.map((row) => {
                const isProgramDone = row.progress >= 100;
                const visibilityExpiresAt = row.visibilityExpiresAt;
                const hasVisibilityExpiration = visibilityExpiresAt !== null;
                const durationLabel = getDurationLabel(row);
                const hasDurationLabel = Boolean(durationLabel);
                const isSelected = selectedProgramId === row.id;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setSelectedProgramId(row.id)}
                    aria-current={isSelected ? "true" : undefined}
                    className={cn(
                      "border-border/70 bg-linear-to-br w-full cursor-pointer rounded-xl border p-3 text-left transition-all duration-200",
                      getMoodPalette(row.program.title, row.program.description),
                      isSelected
                        ? "border-primary shadow-md ring-2 ring-primary/25"
                        : "hover:border-primary/40 hover:shadow-md",
                    )}
                  >
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold leading-snug">
                          {row.program.title}
                        </span>
                        {row.program.contentMode === "PDF" ? (
                          <Badge
                            variant="secondary"
                            className="shrink-0 text-[10px]"
                          >
                            {t("programs.deliveryModePdf", "PDF")}
                          </Badge>
                        ) : null}
                        {isProgramDone ? (
                          <Badge
                            variant="default"
                            className="shrink-0 text-[10px]"
                          >
                            {t("status.done")}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                        <span>
                          {t("programs.assignedOn")}{" "}
                          {new Date(row.assignedAt).toLocaleDateString(
                            i18n.language,
                          )}
                        </span>
                        {hasVisibilityExpiration ? (
                          <span>
                            {t("programs.visibleUntil")}{" "}
                            {new Date(visibilityExpiresAt).toLocaleDateString(
                              i18n.language,
                            )}
                          </span>
                        ) : null}
                        {hasDurationLabel ? (
                          <span>Durée : {durationLabel}</span>
                        ) : null}
                        {row.program.org?.name ? (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {row.program.org.name}
                          </span>
                        ) : null}
                      </div>
                      <div className="space-y-1.5 pt-0.5">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-muted-foreground text-[11px] font-medium">
                            {t("programs.progress")}
                          </span>
                          <span className="text-xs font-semibold tabular-nums tracking-tight">
                            {row.progress}%
                          </span>
                        </div>
                        <div
                          className="bg-muted/80 ring-border/50 h-2 w-full overflow-hidden rounded-full ring-1 ring-inset"
                          role="progressbar"
                          aria-valuenow={row.progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={`${t("programs.progress")} ${row.progress}%`}
                        >
                          <div
                            className={cn(
                              "h-full rounded-full transition-[width] duration-500 ease-out",
                              row.progress >= 100
                                ? "bg-emerald-500"
                                : row.progress > 0
                                  ? "from-primary to-primary/85 bg-linear-to-r"
                                  : "bg-muted-foreground/30",
                            )}
                            style={{ width: `${clamp(row.progress)}%` }}
                          />
                        </div>
                      </div>
                      {row.program.description?.trim() ? (
                        <p className="text-muted-foreground line-clamp-2 text-xs leading-relaxed">
                          {row.program.description
                            .replace(/[#*_~`>]/g, "")
                            .slice(0, 150)}
                        </p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
          <section
            className="min-w-0 space-y-3"
            aria-labelledby="programs-program-detail-title"
          >
            <h2
              id="programs-program-detail-title"
              className="text-foreground px-0.5 text-sm font-semibold tracking-tight"
            >
              {t("programs.sectionServiceDetail")}
            </h2>
            {(() => {
              const row = displayedPrograms.find(
                (p) => p.id === selectedProgramId,
              );
              if (!row) {
                return (
                  <Card className="border-border/60 bg-muted/15 border-dashed">
                    <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
                      <EmptyCartIllustration size={140} className="mx-auto" />
                      <p className="text-muted-foreground max-w-sm text-sm leading-relaxed">
                        {t("programs.emptyServiceDetail")}
                      </p>
                    </CardContent>
                  </Card>
                );
              }
              const isProgramDone = row.progress >= 100;
              const visibilityExpiresAt = row.visibilityExpiresAt;
              const hasVisibilityExpiration = visibilityExpiresAt !== null;
              const durationLabel = getDurationLabel(row);
              const hasDurationLabel = Boolean(durationLabel);
              const stepGroups = (() => {
                const ordered = [...row.steps].sort(
                  (a, b) => a.position - b.position,
                );
                return groupStepsByContiguousGroupTitle(ordered).map((g) => {
                  const meta = parseBlockGroupMeta(g.items[0]?.groupDescription);
                  return {
                    key: `${g.startIndex}-${g.key}`,
                    label: g.label,
                    steps: g.items,
                    headline: meta.workoutType
                      ? buildCustomWorkoutHeadline(
                          meta.workoutType,
                          meta.workoutParams ?? {},
                        )
                      : null,
                    instruction: meta.instruction,
                  };
                });
              })();
              const hasNamedGroups = stepGroups.some((group) => group.label);

              return (
                <Card
                  key={row.id}
                  className={cn(
                    "border-border/70 bg-linear-to-br shadow-md transition-all duration-200",
                    getMoodPalette(row.program.title, row.program.description),
                  )}
                >
                  <CardHeader className="pb-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <CardTitle className="text-lg leading-snug">
                          {row.program.title}
                        </CardTitle>
                        {row.program.contentMode === "PDF" ? (
                          <Badge
                            variant="secondary"
                            className="shrink-0 text-[10px]"
                          >
                            {t("programs.deliveryModePdf", "PDF")}
                          </Badge>
                        ) : null}
                        {isProgramDone ? (
                          <Badge
                            variant="default"
                            className="shrink-0 text-[10px]"
                          >
                            {t("status.done")}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                        <span>
                          {t("programs.assignedOn")}{" "}
                          {new Date(row.assignedAt).toLocaleDateString(
                            i18n.language,
                          )}
                        </span>
                        {hasVisibilityExpiration ? (
                          <span>
                            {t("programs.visibleUntil")}{" "}
                            {new Date(visibilityExpiresAt).toLocaleDateString(
                              i18n.language,
                            )}
                          </span>
                        ) : null}
                        {hasDurationLabel ? (
                          <span>Durée : {durationLabel}</span>
                        ) : null}
                        {row.program.org?.name ? (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {row.program.org.name}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 space-y-1.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-muted-foreground text-[11px] font-medium">
                          {t("programs.progress")}
                        </span>
                        <span className="text-xs font-semibold tabular-nums tracking-tight">
                          {row.progress}%
                        </span>
                      </div>
                      <div
                        className="bg-muted/80 ring-border/50 h-2 w-full overflow-hidden rounded-full ring-1 ring-inset"
                        role="progressbar"
                        aria-valuenow={row.progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${t("programs.progress")} ${row.progress}%`}
                      >
                        <div
                          className={cn(
                            "h-full rounded-full transition-[width] duration-500 ease-out",
                            row.progress >= 100
                              ? "bg-emerald-500"
                              : row.progress > 0
                                ? "from-primary to-primary/85 bg-linear-to-r"
                                : "bg-muted-foreground/30",
                          )}
                          style={{ width: `${clamp(row.progress)}%` }}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                      {row.program.description?.trim() ? (
                        <MarkdownContent
                          content={row.program.description}
                          className="text-muted-foreground text-sm"
                        />
                      ) : null}
                      {row.program.contentMode === "PDF" ? (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const previewWindow = window.open(
                                "about:blank",
                                "_blank",
                              );
                              if (!previewWindow) {
                                toast.error(
                                  t(
                                    "programs.pdfPopupBlocked",
                                    "Autorise les fenêtres pop-up pour afficher le PDF, ou utilise le téléchargement.",
                                  ),
                                );
                                return;
                              }
                              try {
                                const blob = await fetchAssignmentPdfBlob(
                                  row.id,
                                );
                                const url = URL.createObjectURL(blob);
                                previewWindow.location.href = url;
                                window.setTimeout(
                                  () => URL.revokeObjectURL(url),
                                  120_000,
                                );
                              } catch {
                                previewWindow.close();
                                toast.error(
                                  t(
                                    "programs.pdfOpenFailed",
                                    "Impossible d'ouvrir le PDF.",
                                  ),
                                );
                              }
                            }}
                          >
                            <Eye className="h-4 w-4" />
                            {t("programs.viewPdf", "Voir le PDF")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const blob = await fetchAssignmentPdfBlob(
                                  row.id,
                                );
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = buildProgramPdfDownloadFilename(
                                  row.program.title,
                                  t(
                                    "programs.downloadPdfFilenameFallback",
                                    "programme",
                                  ),
                                );
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              } catch {
                                /* ignore */
                              }
                            }}
                          >
                            <Download className="h-4 w-4" />
                            {t("programs.downloadPdf", "Télécharger")}
                          </Button>
                        </div>
                      ) : null}
                      {row.completedCyclesCount > 0 ? (
                        <div className="border-border/60 bg-muted/20 space-y-2 rounded-lg border px-3 py-2.5">
                          <p className="text-muted-foreground text-xs font-medium">
                            {t("programs.completionHistoryTitle")}
                          </p>
                          <p className="text-foreground text-xs">
                            {t("programs.completionCount", {
                              count: row.completedCyclesCount,
                            })}
                          </p>
                          <ul className="text-muted-foreground list-inside list-disc space-y-1 text-xs">
                            {(row.completionCycles ?? []).map((c) => (
                              <li key={c.id}>
                                {t("programs.completionHistoryItem", {
                                  cycle: c.cycleIndex,
                                  percent: c.progressPercent,
                                  date: new Date(
                                    c.completedAt,
                                  ).toLocaleDateString(i18n.language),
                                })}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {row.program.contentMode !== "PDF" &&
                        !shouldUseProgramCalendarView(
                          row.sessions ?? [],
                          row.completedCyclesCount,
                        ) &&
                        (() => {
                          const programCheckboxSteps = row.steps.filter(
                            (s) => s.type === "CHECKBOX",
                          );
                          const allProgramCheckboxesDone =
                            programCheckboxSteps.length > 0 &&
                            programCheckboxSteps.every((s) => s.isDone);
                          const bulkBusyThisProgram = bulkProgramId === row.id;
                          if (programCheckboxSteps.length === 0) return null;
                          const doneCount = programCheckboxSteps.filter(
                            (s) => s.isDone,
                          ).length;
                          return (
                            <div className="border-border/60 bg-muted/25 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                <p className="text-muted-foreground text-xs font-medium">
                                  {t("programs.cardBulkSteps")}
                                </p>
                                <span className="text-muted-foreground/70 text-[10px] tabular-nums">
                                  {doneCount}/{programCheckboxSteps.length}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-[11px]">
                                  {t("programs.groupCheckAll")}
                                </span>
                                <Switch
                                  checked={allProgramCheckboxesDone}
                                  disabled={
                                    bulkBusyThisProgram || updateStep.isPending
                                  }
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setConfirmCheckAll({
                                        programId: row.id,
                                        clientId: row.clientId,
                                        steps: row.steps,
                                      });
                                    } else {
                                      void setProgramCheckboxStepsDone(
                                        row.id,
                                        row.clientId,
                                        row.steps,
                                        false,
                                      );
                                    }
                                  }}
                                  aria-label={t("programs.groupCheckAll")}
                                />
                              </div>
                            </div>
                          );
                        })()}
                      {row.program.contentMode !== "PDF" &&
                      shouldUseProgramCalendarView(
                        row.sessions ?? [],
                        row.completedCyclesCount,
                      ) ? (
                        <ProgramCalendarView
                          programId={row.id}
                          title={row.program.title}
                          sessions={row.sessions ?? []}
                          completedCyclesCount={row.completedCyclesCount}
                          clientId={row.clientId}
                          onViewExerciseGuide={setViewingExercise}
                          onMarkModified={() => markProgramAsLastModified(row.id)}
                        />
                      ) : (
                        <>
                      {renderSessionsTimeline(
                        row.id,
                        row.clientId,
                        row.sessions ?? [],
                        row.completedCyclesCount,
                      )}
                      {row.steps.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 py-6 text-center">
                          <EmptyCartIllustration size={112} className="mx-auto" />
                          <p className="text-muted-foreground max-w-sm text-xs leading-relaxed">
                            {t("programs.noStep")}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {stepGroups.map((group) => {
                            const groupComplete =
                              group.steps.length > 0 &&
                              group.steps.every(isStepCompleted);
                            const hasGroupHeader = Boolean(
                              group.label || hasNamedGroups,
                            );
                            const doneInGroup =
                              group.steps.filter(isStepCompleted).length;
                            const accordionKey = `${row.id}::${group.key}`;
                            const defaultGroupExpanded = !groupComplete;
                            const groupExpanded = hasGroupHeader
                              ? (groupAccordionOpen[accordionKey] ??
                                defaultGroupExpanded)
                              : true;
                            const groupStepsDomId = `program-${row.id}-group-${group.key.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

                            return (
                              <div key={group.key} className="space-y-3">
                                {hasGroupHeader ? (
                                  <div
                                    className={cn(
                                      "overflow-hidden rounded-lg border",
                                      groupComplete
                                        ? "border-emerald-500/35 bg-emerald-500/10"
                                        : "bg-muted/60 border-border/60",
                                    )}
                                  >
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      className={cn(
                                        "relative flex h-auto w-full items-center justify-start gap-2.5 rounded-none px-3 py-2.5 text-left font-normal shadow-none transition-colors sm:px-4 sm:py-3",
                                        "focus-visible:ring-ring focus-visible:ring-2",
                                        groupComplete
                                          ? "hover:bg-emerald-500/15"
                                          : "hover:bg-muted/80",
                                      )}
                                      aria-expanded={groupExpanded}
                                      aria-controls={groupStepsDomId}
                                      aria-label={
                                        groupExpanded
                                          ? t(
                                              "programs.groupAccordionCollapse",
                                              "Replier le groupe",
                                            )
                                          : t(
                                              "programs.groupAccordionExpand",
                                              "Déplier le groupe",
                                            )
                                      }
                                      onClick={() => {
                                        setGroupAccordionOpen((prev) => {
                                          const current =
                                            prev[accordionKey] ??
                                            defaultGroupExpanded;
                                          return {
                                            ...prev,
                                            [accordionKey]: !current,
                                          };
                                        });
                                      }}
                                    >
                                      {groupComplete ? (
                                        <CheckCircle2
                                          className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400"
                                          aria-hidden
                                        />
                                      ) : (
                                        <Layers
                                          className="text-primary h-5 w-5 shrink-0"
                                          aria-hidden
                                        />
                                      )}
                                      <div className="flex min-w-0 flex-1 items-center gap-2.5 pr-9 sm:pr-10">
                                        <p className="text-foreground min-w-0 flex-1 text-sm font-bold leading-snug sm:text-base">
                                          {group.label ??
                                            t(
                                              "programs.ungroupedSection",
                                              "Étapes sans groupe",
                                            )}
                                        </p>
                                        <span
                                          className={cn(
                                            "shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums",
                                            groupComplete
                                              ? "text-foreground border-emerald-500/45 bg-emerald-500/15"
                                              : "text-foreground/80 bg-background/80 border-border/60",
                                          )}
                                        >
                                          {t(
                                            "programs.groupStepRatio",
                                            "{{done}}/{{total}}",
                                            {
                                              done: doneInGroup,
                                              total: group.steps.length,
                                            },
                                          )}
                                        </span>
                                      </div>
                                      <ChevronDown
                                        className={cn(
                                          "text-muted-foreground absolute right-3 top-2.5 h-5 w-5 shrink-0 transition-transform duration-200 sm:right-4 sm:top-3",
                                          groupExpanded ? "rotate-180" : "",
                                        )}
                                        aria-hidden
                                      />
                                    </Button>
                                    {groupExpanded &&
                                    (group.headline ||
                                      group.instruction ||
                                      group.steps[0]?.groupMediaUrl?.trim()) ? (
                                      <div className="border-border/40 space-y-3 border-t px-3 py-3 pl-10 sm:px-4 sm:pl-12">
                                        {group.headline ? (
                                          <p className="text-foreground text-sm font-semibold leading-snug">
                                            {group.headline}
                                          </p>
                                        ) : null}
                                        {group.instruction ? (
                                          <p
                                            className={cn(
                                              "whitespace-pre-line text-sm font-medium leading-snug",
                                              groupComplete
                                                ? "text-foreground/90"
                                                : "text-foreground/80",
                                            )}
                                          >
                                            {group.instruction}
                                          </p>
                                        ) : null}
                                        {group.steps[0]?.groupMediaUrl?.trim() ? (
                                          <ProgramBlockMedia
                                            url={group.steps[0].groupMediaUrl}
                                          />
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}
                                <div
                                  id={
                                    hasGroupHeader ? groupStepsDomId : undefined
                                  }
                                  className={cn(
                                    "grid gap-2",
                                    "grid-cols-1",
                                    hasGroupHeader && "ml-4 sm:ml-8",
                                    hasGroupHeader &&
                                      !groupExpanded &&
                                      "hidden",
                                  )}
                                >
                                  {group.steps.map((step) =>
                                    (() => {
                                      const isCheckboxStep =
                                        step.type === "CHECKBOX";
                                      const isStepPending =
                                        updateStep.isPending &&
                                        pendingStepId === step.id;

                                      return (
                                        <div
                                          key={step.id}
                                          className={cn(
                                            "relative rounded-xl border p-3 transition-all",
                                            step.isDone
                                              ? "border-emerald-500/30 bg-emerald-500/5"
                                              : "border-border/80 bg-background/80 hover:border-primary/30 hover:bg-background",
                                            isCheckboxStep
                                              ? "cursor-pointer select-none"
                                              : "",
                                          )}
                                          role={
                                            isCheckboxStep
                                              ? "button"
                                              : undefined
                                          }
                                          tabIndex={isCheckboxStep ? 0 : -1}
                                          aria-pressed={
                                            isCheckboxStep
                                              ? step.isDone
                                              : undefined
                                          }
                                          onClick={() => {
                                            if (!isCheckboxStep) return;
                                            toggleStepDone(
                                              row.id,
                                              step.id,
                                              step.isDone,
                                              row.clientId,
                                            );
                                          }}
                                          onKeyDown={(e) => {
                                            if (!isCheckboxStep) return;
                                            if (
                                              e.key !== "Enter" &&
                                              e.key !== " "
                                            )
                                              return;
                                            e.preventDefault();
                                            toggleStepDone(
                                              row.id,
                                              step.id,
                                              step.isDone,
                                              row.clientId,
                                            );
                                          }}
                                        >
                                          {celebratingStepId === step.id ? (
                                            <div className="pointer-events-none absolute -top-2 right-2 flex animate-bounce items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700 shadow-sm">
                                              <PartyPopper className="h-3.5 w-3.5" />
                                              Bravo !
                                            </div>
                                          ) : null}
                                          <div className="flex items-start gap-2">
                                            <div className="pt-px">
                                              <Checkbox
                                                checked={step.isDone}
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                className="mt-0.5 shrink-0"
                                                disabled={
                                                  !isCheckboxStep ||
                                                  isStepPending
                                                }
                                                onCheckedChange={() =>
                                                  toggleStepDone(
                                                    row.id,
                                                    step.id,
                                                    step.isDone,
                                                    row.clientId,
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="min-w-0 flex-1 space-y-1.5">
                                              <div className="flex flex-wrap items-center justify-between gap-1.5">
                                                <span
                                                  className={cn(
                                                    "line-clamp-1 text-sm font-semibold leading-snug sm:text-[0.9rem]",
                                                    step.isDone
                                                      ? "text-muted-foreground line-through"
                                                      : "",
                                                  )}
                                                >
                                                  {step.title}
                                                </span>
                                                {step.exercise ? (
                                                  <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setViewingExercise(
                                                        step.exercise,
                                                      );
                                                    }}
                                                    className="text-primary hover:bg-primary/10 inline-flex h-auto shrink-0 gap-1 px-1.5 py-0.5 text-[10px] font-medium shadow-none"
                                                    title={t(
                                                      "exerciseGuide.viewExercise",
                                                    )}
                                                  >
                                                    <Dumbbell className="h-3.5 w-3.5" />
                                                  </Button>
                                                ) : null}
                                                {step.targetValue !== null ? (
                                                  <Badge
                                                    variant="outline"
                                                    className="text-[10px]"
                                                  >
                                                    {step.targetValue}{" "}
                                                    {step.targetUnit ?? ""}
                                                  </Badge>
                                                ) : null}
                                              </div>
                                              {step.description ? (
                                                <MarkdownContent
                                                  content={step.description}
                                                  className="text-foreground/90 text-sm"
                                                />
                                              ) : null}
                                              {step.type === "TEXT" ? (
                                                <textarea
                                                  value={readDraft(
                                                    step.id,
                                                    step.valueText ?? "",
                                                  )}
                                                  onChange={(e) =>
                                                    setDraftByStep((prev) => ({
                                                      ...prev,
                                                      [step.id]: e.target.value,
                                                    }))
                                                  }
                                                  onBlur={() => (
                                                    markProgramAsLastModified(
                                                      row.id,
                                                    ),
                                                    updateStep.mutate({
                                                      id: step.id,
                                                      data: {
                                                        valueText: readDraft(
                                                          step.id,
                                                          step.valueText ?? "",
                                                        ),
                                                      },
                                                      assignmentClientId:
                                                        row.clientId,
                                                    })
                                                  )}
                                                  placeholder={t(
                                                    "programs.enterProgress",
                                                  )}
                                                  className="bg-muted/50 focus-visible:border-ring focus-visible:ring-ring min-h-20 w-full rounded-md border border-transparent px-3 py-2 text-xs focus-visible:ring-2"
                                                />
                                              ) : null}
                                              {step.type === "NUMBER" ||
                                              step.type === "DURATION" ? (
                                                <input
                                                  type="number"
                                                  inputMode="decimal"
                                                  value={readDraft(
                                                    step.id,
                                                    step.valueNumber?.toString() ??
                                                      "",
                                                  )}
                                                  onChange={(e) =>
                                                    setDraftByStep((prev) => ({
                                                      ...prev,
                                                      [step.id]: e.target.value,
                                                    }))
                                                  }
                                                  onBlur={() => {
                                                    markProgramAsLastModified(
                                                      row.id,
                                                    );
                                                    const raw = readDraft(
                                                      step.id,
                                                      step.valueNumber?.toString() ??
                                                        "",
                                                    );
                                                    const parsed =
                                                      raw.trim() === ""
                                                        ? null
                                                        : Number(raw);
                                                    updateStep.mutate({
                                                      id: step.id,
                                                      data: {
                                                        valueNumber:
                                                          Number.isFinite(
                                                            parsed,
                                                          )
                                                            ? parsed
                                                            : null,
                                                      },
                                                      assignmentClientId:
                                                        row.clientId,
                                                    });
                                                  }}
                                                  className="bg-muted/50 focus-visible:border-ring focus-visible:ring-ring h-10 w-full rounded-md border border-transparent px-3 text-xs focus-visible:ring-2"
                                                />
                                              ) : null}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })(),
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                        </>
                      )}
                    </CardContent>
                </Card>
              );
            })()}
          </section>
        </div>
        </>
      )}

      <ExerciseGuideDialog
        exercise={viewingExercise}
        open={viewingExercise !== null}
        onClose={() => setViewingExercise(null)}
      />

      <ClientSessionDetailModal
        open={openSession !== null}
        onOpenChange={(o) => {
          if (!o) setOpenSession(null);
        }}
        session={openSession?.session ?? null}
        clientId={openSession?.clientId ?? ""}
        clientNotesEnabled={clientNotesEnabled}
      />

      <Dialog
        open={confirmCheckAll !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmCheckAll(null);
        }}
      >
        <DialogContent className="mx-4 max-w-md sm:mx-auto">
          <DialogHeader>
            <DialogTitle>{t("programs.confirmCheckAllTitle")}</DialogTitle>
            <DialogDescription>
              {t("programs.confirmCheckAllDescription")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCheckAll(null)}>
              {t("programs.confirmCheckAllCancel")}
            </Button>
            <Button
              onClick={() => {
                if (confirmCheckAll) {
                  void setProgramCheckboxStepsDone(
                    confirmCheckAll.programId,
                    confirmCheckAll.clientId,
                    confirmCheckAll.steps,
                    true,
                  );
                }
                setConfirmCheckAll(null);
              }}
            >
              {t("programs.confirmCheckAllAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
